import React from 'react';
import {
 Users,
 DollarSign,
 Calendar,
 Package,
 CreditCard,
 CheckCircle,
 ArrowUpRight,
 ArrowDownRight,
 Lock,
 Crown,
} from 'lucide-react';
import { PRIVATE_PAYOUT_EXCLUDED_NOTE } from '../../../utils/bookingSource';

const formatCurrency = (amount) => {
 if (amount === null || amount === undefined || amount === '') return '$0.00';
 const numAmount = typeof amount === 'string' ? parseFloat(amount) : Number(amount);
 if (isNaN(numAmount) || !isFinite(numAmount)) return '$0.00';
 return `$${numAmount.toLocaleString('en-US', {
 minimumFractionDigits: 2,
 maximumFractionDigits: 2,
 })}`;
};

const toneStyles = {
 emerald: {
 wrap: 'bg-surface-muted text-ink',
 },
 sky: {
 wrap: 'bg-surface-muted text-ink',
 },
 amber: {
 wrap: 'bg-surface-muted text-ink',
 },
 gray: {
 wrap: 'bg-surface-muted text-ink',
 },
};

const StatCard = ({ title, value, subtitle, icon: Icon, tone = 'gray', change }) => {
 const styles = toneStyles[tone] || toneStyles.gray;
 return (
 <div className="product-panel p-4 sm:p-5">
 <div className="mb-3 flex items-start justify-between gap-3">
 <div className={`shrink-0 rounded-product-md border border-border p-2 ${styles.wrap}`}>
 <Icon className="h-4 w-4" />
 </div>
 {change !== null && change !== undefined ? (
 <div
 className={`inline-flex items-center gap-0.5 text-[11px] font-semibold tabular-nums ${ change >= 0 ? 'text-ink' : 'text-red-600' }`}
 >
 {change >= 0 ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
 {Math.abs(change).toFixed(1)}%
 </div>
 ) : null}
 </div>
 <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-tertiary">
 {title}
 </p>
 <p className="mt-1 text-xl font-bold tracking-tight text-ink tabular-nums sm:text-2xl">
 {value}
 </p>
 {subtitle ? (
 <p className="mt-1 text-xs leading-snug text-ink-tertiary">{subtitle}</p>
 ) : null}
 </div>
 );
};

const KeyStatistics = ({ analytics, amountsVisible = true }) => {
 const overview = analytics?.overview || {};
 const bookingsAnalytics = analytics?.bookings_analytics || {};
 const revenueAnalytics = analytics?.revenue_analytics || {};
 const userAnalytics = analytics?.user_analytics || {};
 const vendorAnalytics = analytics?.vendor_analytics || {};

 const privateOffline =
 overview.private_offline_collected ?? revenueAnalytics.private_offline_collected ?? 0;
 const privateCount = overview.private_bookings_count ?? 0;

 const primary = [
 {
 key: 'revenue',
 isAmount: true,
 title: 'Platform revenue',
 value: formatCurrency(
 overview.platform_revenue ?? overview.total_revenue ?? revenueAnalytics.total_revenue ?? 0
 ),
 subtitle: 'Excludes private bookings',
 icon: DollarSign,
 tone: 'emerald',
 change: revenueAnalytics.revenue_growth ?? null,
 },
 {
 key: 'subscriptions',
 isAmount: true,
 title: 'Subscriptions',
 value: formatCurrency(overview.subscription_revenue ?? 0),
 subtitle: 'SaaS payments collected',
 icon: Crown,
 tone: 'sky',
 },
 {
 key: 'bookings',
 isAmount: false,
 title: 'Bookings',
 value: overview.total_bookings || bookingsAnalytics.total || 0,
 icon: Calendar,
 tone: 'sky',
 change: bookingsAnalytics.growth_rate ?? null,
 },
 {
 key: 'fees',
 isAmount: true,
 title: 'Platform fees',
 value: formatCurrency(overview.platform_fees || revenueAnalytics.platform_fees || 0),
 subtitle: 'Commission retained',
 icon: CreditCard,
 tone: 'amber',
 },
 {
 key: 'completed',
 isAmount: false,
 title: 'Completed',
 value: overview.completed_bookings || bookingsAnalytics.by_status?.completed || 0,
 subtitle: 'Platform events finished',
 icon: CheckCircle,
 tone: 'emerald',
 },
 ];

 const secondary = [
 {
 key: 'subscribers',
 isAmount: false,
 title: 'Subscribed vendors',
 value: overview.active_subscribers ?? 0,
 subtitle:
 overview.subscribers_in_period != null
 ? `${overview.subscribers_in_period} started this period`
 : 'Active paid/free plans',
 icon: Crown,
 tone: 'gray',
 },
 {
 key: 'users',
 isAmount: false,
 title: 'Users',
 value: overview.total_users || userAnalytics.total_users || 0,
 icon: Users,
 tone: 'gray',
 change: userAnalytics.growth_rate ?? null,
 },
 {
 key: 'vendors',
 isAmount: false,
 title: 'Vendors',
 value: overview.total_vendors || vendorAnalytics.total_vendors || 0,
 icon: Package,
 tone: 'gray',
 change: vendorAnalytics.growth_rate ?? null,
 },
 ];

 if (privateOffline > 0 || privateCount > 0) {
 secondary.push({
 key: 'private',
 isAmount: true,
 title: 'Private offline',
 value: formatCurrency(privateOffline),
 subtitle: `${privateCount} booking(s) · ${PRIVATE_PAYOUT_EXCLUDED_NOTE}`,
 icon: Lock,
 tone: 'sky',
 });
 }

 const visiblePrimary = amountsVisible ? primary : primary.filter((s) => !s.isAmount);
 const visibleSecondary = amountsVisible ? secondary : secondary.filter((s) => !s.isAmount);

 return (
 <div className="space-y-3">
 {!amountsVisible ? (
 <p className="text-xs text-ink-tertiary px-0.5">
 Amount cards are hidden. Use the eye control to show them again.
 </p>
 ) : null}
 <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
 {visiblePrimary.map(({ key, isAmount: _isAmount, ...stat }) => (
 <StatCard key={key} {...stat} />
 ))}
 </div>
 {visibleSecondary.length > 0 ? (
 <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
 {visibleSecondary.map(({ key, isAmount: _isAmount, ...stat }) => (
 <StatCard key={key} {...stat} />
 ))}
 </div>
 ) : null}
 </div>
 );
};

export default KeyStatistics;
